import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert } from "antd";
import { botAsistenteApi, TWILIO_DEFAULTS } from "../../services/botAsistente.api";
import { getMsg } from "../../utils/antdStatic";
import ActividadSeccion from "./asistenteWhatsapp/ActividadSeccion";
import EstadoModoSeccion from "./asistenteWhatsapp/EstadoModoSeccion";
import ModelosLimitesSeccion from "./asistenteWhatsapp/ModelosLimitesSeccion";
import ModoPruebasSeccion from "./asistenteWhatsapp/ModoPruebasSeccion";
import SimulacionesSeccion from "./asistenteWhatsapp/SimulacionesSeccion";
import { useCarga } from "./asistenteWhatsapp/useCarga";
import { crearLectorSinSolapes, useRelecturaPeriodica } from "./asistenteWhatsapp/useRelecturaPeriodica";

/**
 * AsistenteWhatsappTab — pestaña «Asistente de WhatsApp» de /configuracion (round 2026-10-30, etapa B3 del bot).
 *
 * REGLA DEL DUEÑO: durante la fase de pruebas NADA de lo que entra por WhatsApp es real; lo real es solo la app web.
 * El ajuste `botModoReal` está APAGADO por defecto y solo el superadministrador puede encenderlo (con una frase de
 * confirmación). Por eso lo primero de la pestaña es una NOTA fija, visible sin bajar, que lo dice en lenguaje sencillo.
 *
 * Cinco secciones, cada una en su archivo (carpeta asistenteWhatsapp/): estado y modo, modelos y límites, modo de
 * pruebas, simulaciones y actividad reciente. Este contenedor lee lo que comparten (el modo, los conteos y los ajustes)
 * y SIEMPRE lo vuelve a leer del servidor: después de cualquier cambio, al volver a la ventana o a esta pestaña, con
 * «Actualizar» y cada 45 segundos mientras la pestaña del navegador está visible Y esta pestaña de Configuración es la
 * que se ve. Lo que se ve en pantalla es lo que el servidor dijo, no lo que esta pestaña cree haber hecho: otro
 * administrador pudo haber cambiado el modo en el medio, y si pasa se avisa con un mensaje visible (no solo cambia la etiqueta).
 *
 * Visible para admin y superadmin; `esSuperadmin` solo decide qué interruptores quedan activos — el servidor es quien
 * manda en los permisos (403 SOLO_SUPERADMIN).
 *
 * Etapa B3c (observaciones de los revisores de B3b):
 *   · cambio de modo hecho por otra persona: además del aviso de arriba, un aviso emergente largo (12 s) y con clave, vía
 *     `getMsg()` (utils/antdStatic: el `message` estático de "antd" no se dibuja con React 19), porque quien tiene la página
 *     desplazada hacia abajo (lo normal en un celular) no ve el aviso de arriba;
 *   · con el modo desconocido (no se pudo leer o la última lectura de fondo falló) la nota de arriba lo dice en su primera frase
 *     y no tranquiliza;
 *   · las lecturas de fondo pasan por `crearLectorSinSolapes`: una bandera por tipo de lectura, para que un servidor colgado no
 *     apile tres lecturas por latido;
 *   · la lista de simulaciones se relee en silencio junto con los conteos (prop `relectura`).
 */
export interface AsistenteWhatsappTabProps {
  esSuperadmin: boolean;
  /**
   * ¿Es esta la pestaña de Configuración que se está viendo? AntD deja montadas las pestañas ya visitadas aunque estén
   * ocultas; sin esto se seguiría leyendo en segundo plano con otra pestaña abierta. Por defecto sí (uso suelto y pruebas).
   */
  activa?: boolean;
}

const NOTA_SIMULACION =
  "Mientras el modo real esté apagado, TODO lo que entre por WhatsApp es una SIMULACIÓN: se revisa igual que uno real, pero no se guarda en gastos ni en ninguna tabla real. Lo real es solo lo que haces en la app web.";

/**
 * Primera frase de la nota cuando NO se sabe el modo (no se pudo leer, o la última lectura de fondo falló): la nota condicional del
 * dueño («Mientras esté apagado…») tranquiliza, y con el modo desconocido no se puede tranquilizar a nadie.
 */
const NOTA_MODO_INCIERTO = "No se pudo comprobar si el modo real está encendido.";

/** Clave del aviso emergente de un cambio de modo hecho por otra persona: una sola a la vez (un cambio nuevo reemplaza al anterior). */
const CLAVE_TOAST_CAMBIO_DE_MODO = "asistente-whatsapp-cambio-de-modo";
/** Cuánto dura ese aviso emergente (s): largo a propósito, para quien está mirando otra parte de la página. */
const DURACION_TOAST_CAMBIO_DE_MODO_S = 12;

const AsistenteWhatsappTab: React.FC<AsistenteWhatsappTabProps> = ({ esSuperadmin, activa = true }) => {
  const modo = useCarga(botAsistenteApi.leerModoReal);
  const estado = useCarga(botAsistenteApi.getEstado);
  const ajustes = useCarga(botAsistenteApi.getTwilioSettings);
  /** Sube cuando el modo cambió: las listas (simulaciones, actividad) se vuelven a leer. */
  const [version, setVersion] = useState(0);
  /** Sube en cada lectura de fondo (cada 45 s): la lista de simulaciones relee SOLO la página visible, en silencio, junto con los conteos. */
  const [relectura, setRelectura] = useState(0);
  /** Guarda de las lecturas de fondo: no lanza otra de un tipo mientras la anterior de ese tipo sigue en vuelo (`apiFetch` no tiene tiempo máximo). */
  const [leerSinSolapes] = useState(() => crearLectorSinSolapes());
  /** El modo cambió entre dos lecturas sin que esta persona lo hiciera aquí: se avisa a la vista. */
  const [avisoCambio, setAvisoCambio] = useState<{ ahoraReal: boolean } | null>(null);
  /** El último modo que se supo con certeza (no se borra cuando una lectura falla: sirve para detectar el cambio al volver). */
  const modoVisto = useRef<boolean | null>(null);

  const { recargar: recargarModo } = modo;
  const { recargar: recargarEstado } = estado;
  const { recargar: recargarAjustes } = ajustes;

  // ¿Cambió el modo entre una lectura y la siguiente? Se mira SIEMPRE que llega un valor nuevo (lectura periódica, volver a
  // la ventana, «Actualizar»): antes solo cambiaba la etiqueta y quien tenía la pantalla abierta no se enteraba.
  const datosModo = modo.datos;
  useEffect(() => {
    if (datosModo === null) return;
    const previo = modoVisto.current;
    modoVisto.current = datosModo;
    if (previo !== null && previo !== datosModo) {
      setAvisoCambio({ ahoraReal: datosModo });
      setVersion((v) => v + 1);
      // El aviso de arriba y la nota quedan fuera de la vista si la persona está desplazada hacia abajo (lo normal en un celular, donde
      // la pestaña mide miles de píxeles): un aviso emergente largo y con clave (no se duplica) lo hace visible estén donde estén.
      // Se usa la instancia de AntApp (utils/antdStatic): el `message` estático de "antd" no se dibuja con React 19.
      const configuracion = { key: CLAVE_TOAST_CAMBIO_DE_MODO, duration: DURACION_TOAST_CAMBIO_DE_MODO_S };
      if (datosModo) {
        void getMsg()?.error({ ...configuracion, content: "Acaban de ENCENDER el modo real: desde ahora lo que entre por WhatsApp SÍ se guarda de verdad." });
      } else {
        void getMsg()?.warning({ ...configuracion, content: "Acaban de APAGAR el modo real: desde ahora lo que entre por WhatsApp es una simulación y no se guarda." });
      }
    }
  }, [datosModo]);

  const alCambiarModo = useCallback(async () => {
    const lecturaModo = recargarModo();
    const lecturaEstado = recargarEstado();
    // Un cambio hecho desde esta pantalla no se avisa como ajeno: quien lo hizo ya lo sabe (y vio el aviso de AntD). Se
    // anota justo al llegar la lectura del modo, ANTES de que React pinte el valor nuevo y corra el efecto de arriba (por
    // eso no se espera primero a los conteos: si tardan, el efecto vería un «cambio» que fue nuestro).
    const nuevo = await lecturaModo;
    if (typeof nuevo === "boolean") modoVisto.current = nuevo;
    await lecturaEstado;
    setVersion((v) => v + 1);
  }, [recargarModo, recargarEstado]);

  // Lectura de fondo (modo, conteos y ajustes). Si falla con un dato ya leído, no se borra la pantalla: se marca
  // «desactualizado» y se bloquea lo delicado hasta la próxima lectura buena.
  const releerEnSegundoPlano = useCallback(() => {
    // Una bandera por tipo de lectura: un servidor colgado no apila 3 lecturas por latido, y un tipo atascado no frena a los demás
    // (el modo, que es lo importante, se sigue releyendo aunque los ajustes no respondan).
    leerSinSolapes("modo", () => recargarModo({ silenciosa: true }));
    leerSinSolapes("estado", () => recargarEstado({ silenciosa: true }));
    leerSinSolapes("ajustes", () => recargarAjustes({ silenciosa: true }));
    // La lista de simulaciones lleva su propia bandera (SimulacionesSeccion): aquí solo se le avisa que toca.
    setRelectura((r) => r + 1);
  }, [leerSinSolapes, recargarModo, recargarEstado, recargarAjustes]);
  useRelecturaPeriodica(releerEnSegundoPlano, { activa });

  const alCambiarAjustes = useCallback(async () => {
    await recargarAjustes();
  }, [recargarAjustes]);

  const reintentarEstadoYModo = useCallback(() => {
    void recargarModo();
    void recargarEstado();
  }, [recargarModo, recargarEstado]);

  const modoRealEncendido = modo.datos === true;
  // No se sabe el modo: no se pudo leer, o la última lectura de fondo falló y lo que hay es lo último que se supo (puede haber cambiado).
  const modoIncierto = modo.error || modo.desactualizado;

  return (
    <div style={{ maxWidth: 960 }} data-testid="asistente-whatsapp-tab">
      {/* Con el modo real encendido la nota deja de hablar de simulación: lo primero que se lee es que AHORA se guarda de verdad.
          Si no se sabe el modo, lo primero que se lee es eso: la nota condicional del dueño tranquilizaría sin poder asegurarlo. */}
      {modoIncierto ? (
        <Alert
          type={modo.datos === true ? "error" : "warning"}
          showIcon
          style={{ marginBottom: 16 }}
          data-testid="asistente-nota-simulacion"
          message={<b data-testid="asistente-nota-incierta">{NOTA_MODO_INCIERTO}</b>}
          description={
            modo.datos === true
              ? "Lo último que se supo es que estaba ENCENDIDO: lo que entre por WhatsApp podría estar guardándose de verdad. Toca «Actualizar» en «Estado y modo» para volver a comprobarlo."
              : modo.datos === false
                ? "Lo último que se supo es que estaba apagado, pero pudo haber cambiado: hasta comprobarlo, no des por hecho que lo que entre por WhatsApp es una simulación. Toca «Actualizar» en «Estado y modo»."
                : "Hasta comprobarlo, no des por hecho que lo que entre por WhatsApp es una simulación. Toca «Actualizar» en «Estado y modo» para volver a intentarlo."
          }
        />
      ) : modoRealEncendido ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          data-testid="asistente-nota-simulacion"
          message={
            <b data-testid="asistente-nota-real">
              AHORA el modo real está ENCENDIDO: lo que entre por WhatsApp SÍ se guarda de verdad en los gastos de la empresa.
            </b>
          }
          description="Cuenta en las finanzas, igual que lo que haces en la app web. Solo el superadministrador puede volver a la simulación."
        />
      ) : (
        <Alert type="warning" showIcon style={{ marginBottom: 16 }} data-testid="asistente-nota-simulacion" message={NOTA_SIMULACION} />
      )}

      {avisoCambio && (
        <Alert
          type={avisoCambio.ahoraReal ? "error" : "info"}
          showIcon
          closable
          onClose={() => setAvisoCambio(null)}
          style={{ marginBottom: 16 }}
          data-testid="asistente-aviso-cambio-modo"
          message={
            avisoCambio.ahoraReal
              ? "Acaban de ENCENDER el modo real mientras tenías esta pantalla abierta: desde ahora lo que entre por WhatsApp SÍ se guarda de verdad."
              : "Acaban de APAGAR el modo real mientras tenías esta pantalla abierta: desde ahora lo que entre por WhatsApp es una simulación y no se guarda."
          }
        />
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <EstadoModoSeccion
          esSuperadmin={esSuperadmin}
          modoReal={modo.datos}
          cargando={modo.cargando}
          errorModo={modo.error}
          desactualizado={modo.desactualizado}
          estado={estado.datos}
          errorEstado={estado.error}
          onReintentar={reintentarEstadoYModo}
          onCambioTerminado={alCambiarModo}
        />
        <ModelosLimitesSeccion
          esSuperadmin={esSuperadmin}
          ajustes={ajustes.datos?.actual ?? null}
          defaults={ajustes.datos?.defaults ?? TWILIO_DEFAULTS}
          cargando={ajustes.cargando}
          error={ajustes.error}
          onReintentar={() => void recargarAjustes()}
          onGuardado={alCambiarAjustes}
        />
        <ModoPruebasSeccion
          esSuperadmin={esSuperadmin}
          ajustes={ajustes.datos?.actual ?? null}
          cargando={ajustes.cargando}
          error={ajustes.error}
          onReintentar={() => void recargarAjustes()}
          onCambioTerminado={alCambiarAjustes}
        />
        <SimulacionesSeccion
          estado={estado.datos}
          errorEstado={estado.error}
          version={version}
          onVaciado={() => void recargarEstado()}
          onActualizarConteos={() => void recargarEstado()}
          relectura={relectura}
        />
        <ActividadSeccion version={version} guardandoTexto={ajustes.datos?.actual.guardarTextoEventos ?? null} />
      </div>
    </div>
  );
};

export default AsistenteWhatsappTab;
